/**
 * Host transport for the two opaque guest frames. Every render navigates a new
 * document, so old project listeners/timers cannot accumulate between pages.
 * Callers own unique generation values and must observe `ready` (including
 * AbortError on supersession), then bind comment mapping to that generation.
 */
const activeRenders = new WeakMap();
let navigationSequence = 0;

export function renderGuestFrame(
  iframe,
  html,
  { frame = 'review', generation, signal = null, commentMode = false, timeoutMs = 30_000 } = {}
) {
  if (frame !== 'review' && frame !== 'client') throw new TypeError('Unknown guest frame');
  if (typeof html !== 'string') throw new TypeError('Guest preview HTML must be a string');
  if (!(
    (typeof generation === 'string' && generation.length > 0) ||
    (typeof generation === 'number' && Number.isFinite(generation))
  ))
    throw new TypeError('Guest preview requires a render generation');
  const host = iframe.ownerDocument.defaultView;
  if (!host) throw new Error('Guest preview requires a host window');
  activeRenders.get(iframe)?.dispose();
  let disposed = false;
  let settled = false;
  let posted = false;
  const navigationId = String(++navigationSequence);
  let currentCommentMode = Boolean(commentMode);
  let timer;
  let resolveReady;
  let rejectReady;
  const ready = new Promise((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  const isCurrent = () => !disposed && activeRenders.get(iframe) === handle;
  const cleanup = ({ keepAbort = false } = {}) => {
    host.removeEventListener('message', onMessage);
    if (!keepAbort) signal?.removeEventListener('abort', dispose);
    host.clearTimeout(timer);
  };
  const reject = (error) => {
    if (settled) return;
    settled = true;
    disposed = true;
    cleanup();
    if (activeRenders.get(iframe) === handle) activeRenders.delete(iframe);
    rejectReady(error);
  };
  function dispose() {
    if (disposed) return;
    disposed = true;
    cleanup();
    if (activeRenders.get(iframe) === handle) activeRenders.delete(iframe);
    reject(new DOMException('Guest preview superseded or cancelled', 'AbortError'));
  }
  function setCommentMode(value) {
    currentCommentMode = Boolean(value);
    if (!isCurrent() || !posted) return;
    iframe.contentWindow?.postMessage(
      {
        source: 'pb-review-host',
        kind: 'comment-mode',
        generation,
        commentMode: currentCommentMode
      },
      '*'
    );
  }
  function onLoad() {
    if (!isCurrent() || posted) return;
    posted = true;
    iframe.contentWindow?.postMessage(
      {
        source: 'pb-review-host',
        kind: 'render',
        navigationId,
        html,
        generation,
        commentMode: currentCommentMode
      },
      '*'
    );
  }
  function onMessage(event) {
    if (!isCurrent() || event.source !== iframe.contentWindow) return;
    const message = event.data;
    if (message?.source !== 'pb-review-frame') return;
    if (message.kind === 'boot-ready') {
      // WindowProxy identity survives navigation. Only this new document's
      // bootstrap handshake can receive the bundle, never a late old load.
      if (message.navigationId === navigationId) onLoad();
      return;
    }
    if (!posted || message.generation !== generation) return;
    if (message.kind === 'error') {
      reject(new Error(message.message || 'Guest preview could not render'));
      return;
    }
    if (message.kind !== 'ready' || settled) return;
    settled = true;
    cleanup({ keepAbort: true });
    setCommentMode(currentCommentMode);
    resolveReady({
      generation,
      scriptErrors: Number(message.scriptErrors) || 0
    });
  }
  const handle = { generation, ready, dispose, setCommentMode };
  activeRenders.set(iframe, handle);
  if (signal?.aborted) {
    dispose();
    return handle;
  }
  host.addEventListener('message', onMessage);
  signal?.addEventListener('abort', dispose, { once: true });
  timer = host.setTimeout(() => {
    if (isCurrent()) reject(new Error('Guest preview did not become ready'));
  }, timeoutMs);
  const url = new URL(`./${frame}-frame.html`, iframe.ownerDocument.baseURI);
  // A query change creates a new Document; a fragment change would retain the
  // old project runtime. This counter is not a token and carries no project data.
  url.searchParams.set('render', navigationId);
  iframe.setAttribute('sandbox', 'allow-scripts');
  iframe.removeAttribute('srcdoc');
  iframe.src = url.href;
  return handle;
}
